import type {
  AreaFeature, EditableFloorPlan, MultiPolygonCoordinates, PolygonFeature,
} from '../models/editor-feature';

/**
 * Removing geometry that draws nothing, before a save.
 *
 * Stored plans carry outlines with nothing in them — BDO Enschede has two room zones whose
 * coordinates are an empty array — and parts of outlines with fewer than three corners. None of
 * it can be seen or clicked, so an administrator has no way to find it, let alone fix it; yet it
 * made every save of those plans fail with an error about a shape nobody could point to. The
 * server refuses a ring of fewer than three corners outright, so it cannot simply be sent on.
 *
 * What is dropped is only what could never be drawn. A ring is a shape's outline when it comes
 * first in its polygon and a hole when it comes after, so a polygon whose outline is unusable is
 * dropped whole — keeping its holes would turn a hole into an outline.
 */

/** Fewer than this and a ring encloses nothing. The closing repeat of the first corner counts. */
const MINIMUM_RING_VERTICES = 3;

const isDrawableRing = (ring: readonly unknown[]): boolean => ring.length >= MINIMUM_RING_VERTICES;

/** The parts of an outline that can be drawn, or an empty array when none can. */
export const drawableCoordinates = (coordinates: MultiPolygonCoordinates): MultiPolygonCoordinates => coordinates
  .filter((polygon) => polygon.length > 0 && isDrawableRing(polygon[0]))
  .map((polygon) => polygon.filter(isDrawableRing));

export interface UndrawableShapesResult {
  readonly floorPlan: EditableFloorPlan;
  /** Outlines removed whole, because nothing of them could be drawn. */
  readonly removed: AreaFeature[];
}

/**
 * The plan with every undrawable outline removed, and every undrawable part trimmed off.
 *
 * Returns the same plan object when there was nothing to remove, so a clean plan is not
 * rewritten for nothing.
 */
export const withoutUndrawableShapes = (floorPlan: EditableFloorPlan): UndrawableShapesResult => {
  const removed: AreaFeature[] = [];
  let changed = false;

  const clean = <T extends AreaFeature>(features: readonly T[]): T[] => features.flatMap((feature) => {
    const coordinates = drawableCoordinates(feature.coordinates);

    if (!coordinates.length) {
      removed.push(feature);
      changed = true;
      return [];
    }

    const trimmed = coordinates.length !== feature.coordinates.length
      || coordinates.some((polygon, index) => polygon.length !== feature.coordinates[index].length);

    if (!trimmed) {
      return [feature];
    }

    changed = true;
    return [{ ...feature, coordinates }];
  });

  const next: EditableFloorPlan = {
    ...floorPlan,
    border: clean<PolygonFeature>(floorPlan.border),
    rooms: clean<PolygonFeature>(floorPlan.rooms),
    floorBackdrop: clean<PolygonFeature>(floorPlan.floorBackdrop),
    deskShapes: clean(floorPlan.deskShapes),
  };

  return { floorPlan: changed ? next : floorPlan, removed };
};

/** Which kind of outline a message should name: a room, a floor shape, the border or a desk. */
export const areaMessageSuffixOf = (feature: AreaFeature): string => {
  if (feature.kind === 'deskShape') return 'desk-shape';
  if (feature.layer === 'room') return 'room';
  if (feature.layer === 'border') return 'border';
  return 'floor';
};
